import React from 'react';
import { View, Pressable, Linking, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import type { Trip } from '@/services/trips';

type PartnerService = {
  id: string;
  name: string;
  category: 'insurance' | 'compensation' | 'esim' | 'lounge' | 'tours' | 'transport';
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
  affiliateLink: string;
  price?: string;
  badge?: string;
};

type PartnerMarketplaceProps = {
  trip: Trip;
  onServicePress?: (service: PartnerService) => void;
};

/** Shown above partner outbound links; align wording with counsel / ASA–FTC-style disclosure expectations. */
const AFFILIATE_DISCLOSURE =
  'Tripvio may earn a commission when you use these partner links. Offers and prices come from partners, not Tripvio.';

const PARTNER_SERVICES: PartnerService[] = [
  {
    id: 'travel-insurance',
    name: 'Travel Insurance',
    category: 'insurance',
    description: 'Protect your trip with comprehensive travel insurance',
    icon: 'shield-checkmark',
    affiliateLink: 'https://ektatraveling.tpo.lu/K8HxuyhQ',
    price: 'From $50',
    badge: 'Recommended',
  },
  {
    id: 'compensair',
    name: 'Flight compensation',
    category: 'compensation',
    description: 'Check eligibility for compensation on disrupted flights',
    icon: 'cash-outline',
    affiliateLink: 'https://compensair.tpo.lu/GOpBiI8P',
  },
  {
    id: 'esim-airalo',
    name: 'eSIM Data Plan',
    category: 'esim',
    description: 'Stay connected with instant eSIM activation',
    icon: 'phone-portrait',
    affiliateLink: 'https://airalo.tpo.lu/yH2fJojT',
    price: 'From $5',
  },
  {
    id: 'lounge-access',
    name: 'Airport Lounge Access',
    category: 'lounge',
    description: 'Relax in comfort before your flight',
    icon: 'airplane',
    affiliateLink: 'https://www.prioritypass.com/?affiliate=tri flow', // Replace with actual affiliate link
    price: 'From $99/year',
  },
  {
    id: 'tours-tiqets',
    name: 'Tours & Activities',
    category: 'tours',
    description: 'Tickets for museums, attractions, and experiences worldwide',
    icon: 'ticket-outline',
    affiliateLink: 'https://tiqets.tpo.lu/2RimpFA2',
    price: 'Various',
  },
  {
    id: 'wegotrip',
    name: 'WeGoTrip',
    category: 'tours',
    description: 'Self-guided audio tours on your phone — works offline',
    icon: 'headset-outline',
    affiliateLink: 'https://wegotrip.tpo.lu/QGolJi63',
    price: 'Various',
  },
  {
    id: 'taxi-gettransfer',
    name: 'Transfers & taxi',
    category: 'transport',
    description: 'Airport transfers and rides booked in advance',
    icon: 'car-outline',
    affiliateLink: 'https://gettransfer.tpo.lu/T76hgpJr',
    price: 'Various',
  },
  {
    id: 'car-rental-getrentacar',
    name: 'Car rentals',
    category: 'transport',
    description: 'Compare rentals from local owners — flexible cancellation',
    icon: 'car-sport-outline',
    affiliateLink: 'https://getrentacar.tpo.lu/oeBaTvQn',
    price: 'Various',
  },
];

const getCategoryColor = (category: PartnerService['category'], theme: any): string => {
  switch (category) {
    case 'insurance':
      return theme.colors.error;
    case 'compensation':
      return theme.colors.secondary ?? theme.colors.primary;
    case 'esim':
      return theme.colors.primary;
    case 'lounge':
      return theme.colors.warning;
    case 'tours':
      return theme.colors.success;
    case 'transport':
      return theme.colors.info || theme.colors.primary;
    default:
      return theme.colors.primary;
  }
};

export function PartnerMarketplace({ trip, onServicePress }: PartnerMarketplaceProps) {
  const theme = useTheme();

  const handleServicePress = async (service: PartnerService) => {
    // Track the click (you can add analytics here)
    console.log(`[Marketplace] Service clicked: ${service.name}`, {
      tripId: trip.id,
      destination: trip.destination,
      serviceId: service.id,
    });

    // Call optional callback
    onServicePress?.(service);

    // Open affiliate link
    try {
      const url = service.affiliateLink;
      const canOpen = await Linking.canOpenURL(url);
      if (canOpen) {
        await Linking.openURL(url);
      } else {
        console.warn(`[Marketplace] Cannot open URL: ${url}`);
      }
    } catch (error) {
      console.error(`[Marketplace] Error opening link:`, error);
    }
  };

  // Group services by category
  const servicesByCategory = PARTNER_SERVICES.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, PartnerService[]>);

  const categoryLabels: Record<string, string> = {
    insurance: 'Travel Protection',
    compensation: 'Claims',
    esim: 'Connectivity',
    lounge: 'Comfort',
    tours: 'Experiences',
    transport: 'Transportation',
  };

  return (
    <View style={{ marginBottom: theme.spacing(3) }}>
      <View style={{ 
        flexDirection: 'row', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
      }}>
        <View style={{ flex: 1 }}>
          <SubHeading style={{ fontSize: 18, fontWeight: '700', marginBottom: theme.spacing(0.5) }}>
            Travel Services
          </SubHeading>
          <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
            Enhance your trip with these recommended services
          </Body>
          <Caption
            color={theme.colors.textTertiary}
            style={{ fontSize: 11, lineHeight: 15, marginTop: theme.spacing(1) }}
          >
            {AFFILIATE_DISCLOSURE}
          </Caption>
        </View>
      </View>

      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingRight: theme.spacing(2) }}
      >
        {PARTNER_SERVICES.map((service) => {
          const categoryColor = getCategoryColor(service.category, theme);
          
          return (
            <Card
              key={service.id}
              variant="elevated"
              elevation={1}
              style={{
                width: 280,
                marginRight: theme.spacing(2),
                padding: theme.spacing(2),
              }}
            >
              <Pressable
                onPress={() => handleServicePress(service)}
                style={({ pressed }) => ({
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: theme.spacing(1.5) }}>
                  <View
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      backgroundColor: `${categoryColor}15`,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: theme.spacing(1.5),
                    }}
                  >
                    <Ionicons name={service.icon} size={24} color={categoryColor} />
                  </View>
                  
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                      <ThemedText variant="bodyStrong" style={{ fontSize: 16, flex: 1 }}>
                        {service.name}
                      </ThemedText>
                      {service.badge && (
                        <View
                          style={{
                            backgroundColor: theme.colors.primary + '20',
                            paddingHorizontal: theme.spacing(1),
                            paddingVertical: theme.spacing(0.25),
                            borderRadius: 4,
                            marginLeft: theme.spacing(1),
                          }}
                        >
                          <Caption color={theme.colors.primary} style={{ fontSize: 10, fontWeight: '600' }}>
                            {service.badge}
                          </Caption>
                        </View>
                      )}
                    </View>
                    {service.price && (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, marginBottom: theme.spacing(0.5) }}>
                        {service.price}
                      </Caption>
                    )}
                  </View>
                </View>

                <Body color={theme.colors.textSecondary} style={{ fontSize: 13, marginBottom: theme.spacing(1.5) }}>
                  {service.description}
                </Body>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: theme.spacing(1.5),
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.border,
                  }}
                >
                  <ThemedText color={categoryColor} style={{ fontSize: 13, fontWeight: '600' }}>
                    Learn more
                  </ThemedText>
                  <Ionicons name="arrow-forward" size={16} color={categoryColor} />
                </View>
              </Pressable>
            </Card>
          );
        })}
      </ScrollView>
    </View>
  );
}

